import React from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Crown, Users, Mail } from "lucide-react";

const EXEC = [
  { name: "Sara Stivicic", role_en: "President", role_fr: "Présidente" },
  { name: "Maria Aguilar", role_en: "Vice President", role_fr: "Vice-présidente" },
  { name: "Tsitsi Mugadza", role_en: "Treasurer", role_fr: "Trésorière" },
  { name: "Chantal Leblond", role_en: "Recording Secretary", role_fr: "Secrétaire-archiviste" },
];

const STEWARDS = [
  { name: "Glovy Magana" },
  { name: "Grace Cauilan" },
  { name: "Jesus E Castro" },
  { name: "Marsha Fabay" },
  { name: "Mastora Mohammadi" },
];

function Node({ name, role, top }) {
  return (
    <div className="bg-white border-2 border-[#d81b7a] rounded-xl shadow-sm px-5 py-4 text-center min-w-[200px] max-w-[260px] mx-auto" data-testid="org-node">
      {top && <div className="text-[10px] uppercase tracking-widest text-[#d81b7a] font-bold mb-1">{top}</div>}
      <div className="font-display font-bold text-slate-900 text-lg leading-tight">{name}</div>
      {role && <div className="text-xs text-slate-500 mt-1">{role}</div>}
    </div>
  );
}

export default function Executive() {
  const { t, lang } = useLang();
  const president = EXEC[0];
  const vp = EXEC[1];
  const treasurer = EXEC[2];
  const secretary = EXEC[3];

  const rname = (r) => (lang === "fr" ? r.role_fr : r.role_en);

  return (
    <div data-testid="executive-page">
      <section className="bg-gradient-to-br from-pink-50 via-white to-rose-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900">{t("executive.title")}</h1>
          <p className="text-lg text-slate-600 mt-3 max-w-2xl">{t("executive.sub")}</p>
          <div className="h-1 w-20 cupe-gradient rounded-full mt-4" />
        </div>
      </section>

      {/* Executive Committee Organigram */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center gap-3 mb-2">
          <Crown className="text-[#d81b7a]" size={26} />
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{t("executive.exco")}</h2>
        </div>
        <div className="h-1 w-12 cupe-gradient rounded-full mb-12" />

        <div className="relative">
          {/* President at top */}
          <div className="flex justify-center mb-2">
            <div className="cupe-gradient text-white rounded-xl shadow-lg px-6 py-5 text-center min-w-[240px]" data-testid="org-president">
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">{rname(president)}</div>
              <div className="font-display font-bold text-xl leading-tight mt-1">{president.name}</div>
            </div>
          </div>

          {/* Vertical connector */}
          <div className="flex justify-center"><div className="w-px h-10 bg-[#d81b7a]" /></div>

          {/* Horizontal connector to 3 below */}
          <div className="relative">
            <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px bg-[#d81b7a]" />
            <div className="hidden md:flex justify-between w-2/3 mx-auto">
              <div className="w-px h-8 bg-[#d81b7a]" />
              <div className="w-px h-8 bg-[#d81b7a]" />
              <div className="w-px h-8 bg-[#d81b7a]" />
            </div>
          </div>

          {/* Row of 3: VP, Treasurer, Recording Secretary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-2">
            <Node top={rname(vp)} name={vp.name} />
            <Node top={rname(treasurer)} name={treasurer.name} />
            <Node top={rname(secretary)} name={secretary.name} />
          </div>
        </div>
      </section>

      {/* Stewards */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <Users className="text-[#d81b7a]" size={26} />
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{t("executive.stewards")}</h2>
          </div>
          <div className="h-1 w-12 cupe-gradient rounded-full mb-10" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {STEWARDS.map((s) => (
              <div key={s.name} className="bg-white border border-slate-200 rounded-xl px-5 py-6 text-center hover:-translate-y-1 hover:shadow-md transition-all" data-testid="steward-card">
                <div className="w-14 h-14 rounded-full cupe-gradient flex items-center justify-center mx-auto mb-3">
                  <Users className="text-white" size={22} />
                </div>
                <div className="font-display font-semibold text-slate-900">{s.name}</div>
                <div className="text-xs uppercase tracking-widest text-[#d81b7a] font-semibold mt-1">{t("executive.stewardLabel")}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact prompt */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <Mail className="text-[#d81b7a] mx-auto" size={28} />
        <p className="mt-3 text-slate-700">{t("executive.contactPrompt")}</p>
        <a href="mailto:cupelocal4957oakpark@gmail.com" className="mt-3 inline-flex cupe-gradient text-white px-5 py-3 rounded-md font-semibold min-h-[48px] items-center gap-2">
          <Mail size={16} /> cupelocal4957oakpark@gmail.com
        </a>
      </section>
    </div>
  );
}
